<template>
  <div
    ref="elementRef"
    :class="[
      roundedCornersClasses,
      usesPercentHeight && 'flex min-h-0 w-full flex-col overflow-hidden',
      {
        '<md:o:h-$h': usesPercentHeightSmMd,
        'o:lg:h-$h-lg': usesPercentHeightLg,
        'o:h-$h': usesPercentHeightSmMd && usesPercentHeightLg,
        'o:<lg:h-$h': usesPercentHeightSmMd && !usesPercentHeightLg,
      },
    ]"
    :style="percentHeightStyle"
  >
    <div
      v-style:bg="backgroundColor"
      class="relative grid"
      :class="[
        usesPercentHeight && 'min-h-0 flex flex-1 flex-col',
        {
          '<md:overflow-hidden': usesPercentHeightSmMd && alignmentSmall.includes('overlay'),
          'lg:overflow-hidden': usesPercentHeightLg,
        },
      ]"
    >
      <div
        class="relative flex grid-area-stack"
        :class="{
          '<md:overflow-hidden': usesPercentHeightSmMd && alignmentSmall.includes('overlay'),
          'lg:h-full lg:overflow-hidden': usesPercentHeightLg,
          '<md:flex-1 <md:min-h-0': usesPercentHeightSmMd,
          'min-h-0 flex-1': usesPercentHeight,
        }"
      >
        <cms-base-caption v-if="hasCaption" v-bind="captionProps" />
        <base-picture
          v-if="responsiveMedia.images"
          v-bind="responsiveMedia.images"
          class="w-full"
          :fetchpriority
          fit="cover"
          :lazy
          :position="objectPosition"
        />
        <vf-video
          v-else-if="responsiveMedia.video"
          v-bind="responsiveMedia.video"
          ref="videoRef"
          :autoplay="!lazy"
          class="w-full"
          :directives="{ 'v-style': { objectPosition } }"
          fit="cover"
          loop
          :meta="responsiveMedia.video.sm || responsiveMedia.video.meta"
        />
        <cms-shared-hot-zones v-if="hasHotZones" :media />
        <base-link
          v-if="mediaLink && !hasHotZones"
          aria-hidden="true"
          class="absolute-0"
          :data-cta="mediaLink.delocalizedTracking"
          tabindex="-1"
          :target="mediaLink.targetAttribute"
          :to="mediaLink.url"
          @click="handlePromotionClick('img')"
        />
      </div>
      <div
        :class="[...contentAlignment, brandClasses.contentContainer, { 'shrink-0': usesPercentHeight }]"
        :style="{ gap: backdrop ? '4.5rem' : '3rem' }"
      >
        <div
          v-style:bg="backdrop"
          class="f-col gap-y-2"
          :class="[
            alignmentSmall.includes('overlay') ? 'p-2 -mx-2' : '<md:bg-transparent',
            brandClasses.backdropContainer,
            blurClass,
          ]"
        >
          <cms-base-eyebrow
            v-if="eyebrowText || eyebrowTextHtml || eyebrowLogoResponsive"
            :eyebrow-logo-responsive
            :eyebrow-logo-width-responsive
            :eyebrow-text
            :eyebrow-text-color="eyebrowTextColor || subtitleColor"
            :eyebrow-text-html
            :eyebrow-text-style
          />
          <p v-if="overline" v-style:c="overlineColor" :class="overlineStyle" data-test-id="cms-banner-overline">
            {{ overline }}
          </p>
          <component
            :is="titleTag"
            v-if="title"
            v-style:c="titleColor"
            :class="[titleStyle, { 'sr-only': hideTitle }]"
            data-test-id="cms-banner-title"
          >
            <base-link
              v-if="mediaLink"
              :data-cta="mediaLink.delocalizedTracking"
              :target="mediaLink.targetAttribute"
              :to="mediaLink.url"
              @click="handlePromotionClick('title')"
            >
              <cms-plain-text :content field-name="title" />
            </base-link>
            <template v-else>
              <cms-plain-text :content field-name="title" />
            </template>
          </component>
          <p
            v-if="subtitle"
            v-style:c="subtitleColor"
            :class="subtitleStyle"
            data-test-id="cms-banner-text"
          >
            <cms-plain-text :content field-name="subtitle" />
          </p>
          <cms-rich-text v-if="richText" v-style:c="richTextColor || subtitleColor" class="pointer-within" :content="{ richText }" />
        </div>
        <cms-shared-cta-group
          v-if="linkType !== 'No-CTA' && targets.length && !alignmentLarge.includes('center')"
          :class="ctaClass"
          :cta-type
          :equal-target-size
          :layout="['pointer-within gap-4 wrap <md:hidden lg:gap-6 ', ctaFlexFallback]"
          :multilink-style
          :multilink-title
          :targets
          :text-link-style="getTextLinkStyles(content)"
          @click="handlePromotionClick(`cta${$event + 1}`)"
        />
      </div>
    </div>
    <cms-shared-cta-group
      v-if="linkType !== 'No-CTA' && targets.length"
      :class="[ctaClass, { 'shrink-0': usesPercentHeight }]"
      :cta-type
      :equal-target-size
      :layout="['f-col gap-4 px-4 pb-6 md:o:hidden', ctaFlexFallback]"
      :multilink-style
      :multilink-title
      :targets
      :text-link-style="getTextLinkStyles(content)"
      @click="handlePromotionClick(`cta${$event + 1}`)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { SectionProvider } from '#content/contexts/section'
import type {
  BannerThinAlignmentLarge,
  BannerThinAlignmentSmall,
  BannerThinContent
} from '#types/components/cms/banner-thin'
import { DynamicFieldsKey } from '#content/contexts/dynamic-fields'
import { SectionContextKey } from '#content/contexts/section'

const { content, firstElement, index, isPartOfCollection, parentHeadingLevel } = defineProps<{
  content: BannerThinContent
  firstElement?: boolean
  index?: number
  isPartOfCollection?: boolean
  parentHeadingLevel?: number
}>()

const { bannerThin: { brandClasses }, section: { componentCorners } } = useAppConfig().components.cms

const {
  alignmentLarge,
  alignmentSmall,
  backdrop,
  backdropBlur,
  backgroundColor,
  componentPercentHeight,
  countdown,
  ctaType,
  equalTargetSize,
  eyebrowLogoResponsive,
  eyebrowLogoWidthResponsive,
  eyebrowText,
  eyebrowTextHtml,
  eyebrowTextColor,
  eyebrowTextStyle,
  hideTitle,
  linkType,
  media,
  mediaTarget,
  multilinkStyle,
  multilinkTitle,
  name,
  overline,
  overlineColor,
  overlineStyle,
  promotionTracking,
  richText,
  richTextColor,
  subtitle,
  subtitleColor,
  subtitleStyle,
  targets = [],
  title,
  titleColor,
  titleH1,
  titleStyle,
} = content

countdown && provide(DynamicFieldsKey, { countdown })

const { fetchpriority, lazy } = inject(SectionContextKey, {} as SectionProvider)
const { elementRef, handlePromotionClick } = usePromotionTracking(promotionTracking, name)

const videoRef = ref()
const mediaLink = mediaTarget || targets[0]
const responsiveMedia = getResponsiveMedia(media)

const contentAlignmentLarge: Record<BannerThinAlignmentLarge, string> = {
  'left-right': 'md:between',
  'center-center': 'md:justify-center md:text-center'
}

const contentAlignmentSmall: Partial<Record<BannerThinAlignmentSmall, string>> = {
  'left-overlay': '<md:grid-area-stack <md:pointer-events-none <md:grid-area-stack',
  'center-overlay': '<md:pointer-events-none <md:justify-center <md:text-center <md:grid-area-stack',
  'center-below': '<md:justify-center <md:text-center'
}

const ctaAlignmentLarge: Record<BannerThinAlignmentLarge, string> = {
  'left-right': 'md:relative md:col-start-2 md:row-start-1 md:justify-end',
  'center-center': 'md:hidden'
}

const ctaAlignmentSmall: Record<BannerThinAlignmentSmall, string> = {
  'left-overlay': '<md:items-start <md:mt-6',
  'left-below': '<md:items-start',
  'center-overlay': '<md:items-center <md:text-center <md:mt-6',
  'center-below': '<md:items-center <md:text-center'
}

const { hasCaption, captionProps } = getCaptionConfig(content)

// Thin falls back to flex at the breakpoints where CTAs are not equally sized.
const ctaFlexFallback = [
  getValueForBreakpoint('md', equalTargetSize) ? '' : '~md:flex',
  getValueForBreakpoint('lg', equalTargetSize) ? '' : 'lg:flex',
].filter(Boolean).join(' ')

const contentAlignment = [
  contentAlignmentLarge[alignmentLarge],
  contentAlignmentSmall[alignmentSmall]
]

const ctaClass = [
  ctaAlignmentSmall[alignmentSmall],
  ctaAlignmentLarge[alignmentLarge]
]

const titleTag = computed(() => titleH1
  ? 'h1'
  : isPartOfCollection
    ? parentHeadingLevel ? `h${parentHeadingLevel + 1}` : firstElement && index === 0 ? 'h1' : 'h2'
    : firstElement ? 'h1' : 'h2')

const hasHotZones = computed(() => Object.values(media || []).some((media) => media.enableImageMap))

const roundedCornersClasses = getRoundedCorners(content, componentCorners)

const percentHeightHeaderOffsetPx = useHydrationSafeHeaderOffsetPx()

const usesPercentHeightSmMd = hasPercentHeightAtBreakpoint(componentPercentHeight?.small)
const usesPercentHeightLg = hasPercentHeightAtBreakpoint(componentPercentHeight?.large)
const usesPercentHeight = usesPercentHeightSmMd || usesPercentHeightLg
const anchorSmall = componentPercentHeight?.anchorSmall
const anchorLarge = componentPercentHeight?.anchorLarge

const percentHeightStyle = computed(() =>
  getPercentHeightCssVars(componentPercentHeight, percentHeightHeaderOffsetPx.value),
)

const objectPosition = getResponsiveObjectPosition(anchorSmall, anchorLarge)

useAutoplay(videoRef)

const blurClass = backdropBlur && (backdropBlur === 'low' ? 'backdrop-blur-xs' : 'backdrop-blur-md')
</script>
